/**
 * `orderBook.*` wire contract (garment Increment 9, `garment.order-book`).
 *
 * Read-only cross-style risk inbox. Behind `entitledProcedureFor("garment.styles")`.
 * Risk reasons are deterministic; the briefing's AI only rephrases them.
 */
import { z } from "zod";
import { uuid, isoDate } from "../_common.js";

export const riskSeverity = z.enum(["high", "medium"]);

export const riskReason = z.object({
  code: z.string(),
  label: z.string(),
  severity: riskSeverity,
});

export const styleRisk = z.object({
  styleId: uuid,
  styleRef: z.string(),
  name: z.string(),
  buyerName: z.string(),
  status: z.string(),
  exFactoryDate: isoDate.nullable(),
  daysToExFactory: z.number().int().nullable(),
  severity: riskSeverity,
  reasons: z.array(riskReason),
});

export const listRisksInput = z.object({});

export const listRisksOutput = z.object({
  risks: z.array(styleRisk),
  highCount: z.number().int().nonnegative(),
  mediumCount: z.number().int().nonnegative(),
});

export type ListRisksOutput = z.infer<typeof listRisksOutput>;

// ── Overview (the cross-order cockpit) ──────────────────────────────────────

export const overviewRowSeverity = z.enum(["high", "medium", "info"]);

export const overviewRow = z.object({
  styleId: uuid,
  styleRef: z.string(),
  name: z.string(),
  buyerName: z.string(),
  status: z.string(),
  exFactoryDate: isoDate.nullable(),
  daysToExFactory: z.number().int().nullable(),
  orderQty: z.number().int().nonnegative(),
  note: z.string(),
  severity: overviewRowSeverity,
});

export const overviewInput = z.object({});

export const productionPipeline = z.object({
  cutting: z.number().int().nonnegative(),
  sewing: z.number().int().nonnegative(),
  finishing: z.number().int().nonnegative(),
  packing: z.number().int().nonnegative(),
  readyToShip: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});

export const overviewOutput = z.object({
  summary: z.object({
    openOrders: z.number().int().nonnegative(),
    pcsInFlight: z.number().int().nonnegative(),
    shippingThisWeek: z.number().int().nonnegative(),
    onTrackCount: z.number().int().nonnegative(),
    atRiskCount: z.number().int().nonnegative(),
    blendedMarginBps: z.number().int().nullable(),
  }),
  pipeline: productionPipeline,
  shippingThisWeekRows: z.array(overviewRow),
  behindSchedule: z.array(overviewRow),
  behindPlan: z.array(overviewRow),
  qc: z.array(overviewRow),
  lowMargin: z.array(overviewRow),
  readyToDispatch: z.array(overviewRow),
});

export type OverviewOutput = z.infer<typeof overviewOutput>;
